import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import usageService from '../services/usageService';
import {
  DEFAULT_PRESET,
  isValidPreset,
  previousPeriod,
  resolveGranularity,
  resolvePeriod,
  toApiParams,
} from '../utils/usagePeriod';

const SEZIONI = ['summary', 'timeseries', 'byCodexType', 'documents'];

const statoIniziale = () =>
  SEZIONI.reduce((acc, sezione) => ({ ...acc, [sezione]: { data: null, loading: true, error: null } }), {});

/**
 * Carica le quattro sezioni della pagina «Pagine elaborate».
 *
 * Le richieste sono indipendenti (`allSettled`, non `all`): se l'elenco documenti fallisce, KPI e
 * grafico restano sullo schermo invece di sparire tutti insieme. Ogni caricamento ha il suo
 * AbortController, così un cambio rapido di periodo non lascia che la risposta vecchia vinca
 * sull'ultima richiesta.
 */
const useUsagePages = (presetIniziale = DEFAULT_PRESET, { enabled = true } = {}) => {
  const [preset, setPreset] = useState(isValidPreset(presetIniziale) ? presetIniziale : DEFAULT_PRESET);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [sezioni, setSezioni] = useState(statoIniziale);

  const abortRef = useRef(null);
  const richiestaRef = useRef(0);

  const periodo = useMemo(() => resolvePeriod(preset), [preset]);
  const periodoPrecedente = useMemo(
    () => previousPeriod(periodo.from, periodo.to),
    [periodo.from, periodo.to]
  );
  const granularita = useMemo(
    () => resolveGranularity(periodo.from, periodo.to),
    [periodo.from, periodo.to]
  );

  const aggiorna = useCallback((sezione, patch) => {
    setSezioni((precedente) => ({ ...precedente, [sezione]: { ...precedente[sezione], ...patch } }));
  }, []);

  const carica = useCallback(
    async (sezioniDaCaricare = SEZIONI) => {
      // Chi non può vedere la pagina non deve nemmeno interrogare il backend: i quattro endpoint
      // sono admin-only e risponderebbero 403. L'hook sta sopra il controllo di ruolo perché i
      // React hook non possono stare dentro un `if`, quindi il controllo arriva qui.
      if (!enabled) {
        sezioniDaCaricare.forEach((sezione) => aggiorna(sezione, { loading: false }));
        return;
      }

      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      const idRichiesta = richiestaRef.current + 1;
      richiestaRef.current = idRichiesta;

      const params = toApiParams(periodo.from, periodo.to);
      const chiamate = {
        summary: () => usageService.getSummary(params, controller.signal),
        timeseries: () =>
          usageService.getTimeseries({ ...params, granularity: granularita }, controller.signal),
        byCodexType: () => usageService.getByCodexType(params, controller.signal),
        documents: () => usageService.getDocuments({ ...params, page, size }, controller.signal),
      };

      sezioniDaCaricare.forEach((sezione) => aggiorna(sezione, { loading: true, error: null }));

      const esiti = await Promise.allSettled(sezioniDaCaricare.map((sezione) => chiamate[sezione]()));

      // Una risposta che arriva dopo che è partita una richiesta più recente va ignorata.
      if (richiestaRef.current !== idRichiesta) {
        return;
      }

      esiti.forEach((esito, indice) => {
        const sezione = sezioniDaCaricare[indice];
        if (esito.status === 'fulfilled') {
          aggiorna(sezione, { data: esito.value, loading: false, error: null });
          return;
        }
        // L'annullamento non è un errore da mostrare: la richiesta è stata sostituita.
        if (esito.reason?.code === 'ERR_CANCELED' || esito.reason?.name === 'CanceledError') {
          return;
        }
        aggiorna(sezione, { loading: false, error: esito.reason });
      });
    },
    [aggiorna, enabled, granularita, page, periodo.from, periodo.to, size]
  );

  useEffect(() => {
    carica();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [carica]);

  const cambiaPreset = useCallback((nuovoPreset) => {
    setPreset(isValidPreset(nuovoPreset) ? nuovoPreset : DEFAULT_PRESET);
    setPage(0);
  }, []);

  return {
    preset,
    cambiaPreset,
    periodo,
    periodoPrecedente,
    // La granularità da mostrare è quella dichiarata dalla risposta, non quella richiesta:
    // se il backend fa un downgrade, il titolo del grafico deve seguirlo.
    granularita: sezioni.timeseries.data?.granularity ?? granularita,
    summary: sezioni.summary,
    timeseries: sezioni.timeseries,
    byCodexType: sezioni.byCodexType,
    documents: sezioni.documents,
    page,
    size,
    setPage,
    setSize,
    ricarica: carica,
  };
};

export default useUsagePages;
